import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronUp,
  FaExchangeAlt,
  FaPalette,
  FaRobot,
  FaScroll,
} from "react-icons/fa";
import {
  AI_LEVELS,
} from "./chessAI";
import {
  BOARD_THEMES,
  PIECE_SETS,
  RULE_EXPLANATIONS,
} from "./chessThemes";

export default function ChessSidebar({
  gameMode,
  humanColor,
  aiDifficulty,
  aiInfo,
  currentTurnLabel,
  state,
  gameResult,
  history,
  boardTheme,
  pieceSet,
  orientation,
  showCoordinates,
  onAiDifficultyChange,
  onHumanColorChange,
  onBoardThemeChange,
  onPieceSetChange,
  onFlipBoard,
  onToggleCoordinates,
}) {
  const [rulesOpen, setRulesOpen] =
    useState(true);

  const [
    expandedRule,
    setExpandedRule,
  ] = useState(null);

  return (
    <aside className="chess-side">
      <section className="chess-side-card">
        <span className="chess-side-eyebrow">
          Position
        </span>

        <h3>Game Info</h3>

        <div className="chess-info-grid">
          <div>
            <span>Turn</span>
            <strong>
              {currentTurnLabel}
            </strong>
          </div>

          <div>
            <span>Move</span>
            <strong>
              {state.fullmove}
            </strong>
          </div>

          <div>
            <span>50-move count</span>
            <strong>
              {state.halfmove}/100
            </strong>
          </div>

          <div>
            <span>Check</span>
            <strong>
              {gameResult.check
                ? "Yes"
                : "No"}
            </strong>
          </div>
        </div>
      </section>

      {gameMode === "ai" && (
        <section className="chess-side-card">
          <span className="chess-side-eyebrow">
            Solo game
          </span>

          <h3>
            <FaRobot /> Hobbli AI
          </h3>

          <label className="chess-field">
            <span>Difficulty</span>
            <select
              value={aiDifficulty}
              onChange={(event) =>
                onAiDifficultyChange(
                  event.target.value
                )
              }
            >
              {Object.entries(
                AI_LEVELS
              ).map(
                ([id, level]) => (
                  <option
                    value={id}
                    key={id}
                  >
                    {level.label}
                  </option>
                )
              )}
            </select>
          </label>

          <p className="chess-field-help">
            {
              AI_LEVELS[
                aiDifficulty
              ].description
            }
          </p>

          <label className="chess-field">
            <span>Play as</span>
            <select
              value={humanColor}
              onChange={(event) =>
                onHumanColorChange(
                  event.target.value
                )
              }
            >
              <option value="w">
                White
              </option>
              <option value="b">
                Black
              </option>
            </select>
          </label>

          {aiInfo && (
            <div className="chess-ai-metrics">
              <span>
                Search depth{" "}
                <strong>
                  {aiInfo.depth}
                </strong>
              </span>
              <span>
                Nodes{" "}
                <strong>
                  {aiInfo.nodes.toLocaleString()}
                </strong>
              </span>
              <span>
                Think time{" "}
                <strong>
                  {aiInfo.elapsedMs}ms
                </strong>
              </span>
            </div>
          )}
        </section>
      )}

      <section className="chess-side-card chess-history-card">
        <span className="chess-side-eyebrow">
          <FaScroll /> Notation
        </span>

        <h3>Move History</h3>

        {history.length === 0 ? (
          <p className="chess-empty-history">
            Moves will appear here as
            the game progresses.
          </p>
        ) : (
          <div className="chess-move-list">
            {Array.from({
              length: Math.ceil(
                history.length / 2
              ),
            }).map((_, index) => (
              <div
                className="chess-move-row"
                key={index}
              >
                <span>
                  {index + 1}.
                </span>
                <strong>
                  {history[
                    index * 2
                  ] || ""}
                </strong>
                <strong>
                  {history[
                    index * 2 + 1
                  ] || ""}
                </strong>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="chess-side-card chess-appearance-card">
        <span className="chess-side-eyebrow">
          <FaPalette /> Appearance
        </span>

        <h3>Customize Board</h3>

        <label className="chess-field">
          <span>Board theme</span>
          <select
            value={boardTheme}
            onChange={(event) =>
              onBoardThemeChange(
                event.target.value
              )
            }
          >
            {Object.values(
              BOARD_THEMES
            ).map((theme) => (
              <option
                value={theme.id}
                key={theme.id}
              >
                {theme.name}
              </option>
            ))}
          </select>
        </label>

        <p className="chess-field-help">
          {
            Object.values(
              BOARD_THEMES
            ).find(
              (theme) =>
                theme.id ===
                boardTheme
            )?.description
          }
        </p>

        <label className="chess-field">
          <span>Piece style</span>
          <select
            value={pieceSet}
            onChange={(event) =>
              onPieceSetChange(
                event.target.value
              )
            }
          >
            {Object.values(
              PIECE_SETS
            ).map((set) => (
              <option
                value={set.id}
                key={set.id}
              >
                {set.name}
              </option>
            ))}
          </select>
        </label>

        <div className="chess-appearance-actions">
          <button
            type="button"
            onClick={onFlipBoard}
          >
            <FaExchangeAlt />
            Flip Board
          </button>

          <button
            type="button"
            onClick={
              onToggleCoordinates
            }
          >
            {showCoordinates
              ? "Hide Coordinates"
              : "Show Coordinates"}
          </button>
        </div>

        <span className="chess-orientation-note">
          Viewing from{" "}
          {orientation === "w"
            ? "White"
            : "Black"}'s side
        </span>
      </section>

      <section className="chess-side-card chess-rules-card">
        <button
          type="button"
          className="chess-rules-header"
          onClick={() =>
            setRulesOpen(
              (prev) => !prev
            )
          }
        >
          <div>
            <span className="chess-side-eyebrow">
              Learn as you play
            </span>
            <h3>Chess Rules</h3>
          </div>

          {rulesOpen ? (
            <FaChevronUp />
          ) : (
            <FaChevronDown />
          )}
        </button>

        {rulesOpen && (
          <div className="chess-rule-list">
            {RULE_EXPLANATIONS.map(
              (rule) => {
                const open =
                  expandedRule ===
                  rule.id;

                return (
                  <div
                    className="chess-rule-item"
                    key={rule.id}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedRule(
                          open
                            ? null
                            : rule.id
                        )
                      }
                    >
                      <div>
                        <strong>
                          {rule.title}
                        </strong>
                        <span>
                          {rule.short}
                        </span>
                      </div>

                      {open ? (
                        <FaChevronUp />
                      ) : (
                        <FaChevronDown />
                      )}
                    </button>

                    {open && (
                      <p>
                        {rule.body}
                      </p>
                    )}
                  </div>
                );
              }
            )}
          </div>
        )}
      </section>
    </aside>
  );
}
